'use memo';

import { createFileRoute } from '@tanstack/react-router';
import Icon from '@/components/Icon/Icon';
import { Text } from '@/components/Typography';
import { defaultNS, useTranslation } from '@/lib/i18n';
import { PatternCatalogSectionLogo } from '../-components/PatternCatalogSectionLogo';
import { CATEGORIES, CATEGORY_OWNER_PRODUCTS, type Category, type CategoryTranslationKey } from '../-constants';
import { useAllowIndividualRequestOperationPackagesQueries } from '../-query/useAllowIndividualRequestOperationPackagesQuery';
import { useRequestCatalogByCategory } from '../-query/useRequestCatalogByCategory';
import { RequestsListCard, type RequestCategoryGroup } from './-components/RequestsListCard';
export const Route = createFileRoute('/_individual/individual/request/')({
  component: RequestPage
});
function EmptyState() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.request.emptyState'
  });
  return <div className="flex flex-col items-center justify-center gap-2 py-10 text-center">
      <Icon iconName="FolderOpenIcon" className="h-8 w-8 text-neutral-muted-on-fill" />
      <Text variant="label">{t('title')}</Text>
      <Text variant="pSmall" className="text-neutral-muted-on-fill">
        {t('description')}
      </Text>
    </div>;
}
function toProductGroup(category: Category, items: ReturnType<typeof useRequestCatalogByCategory>['productItems'], label: string): RequestCategoryGroup | null {
  if (items.length === 0) {
    return null;
  }
  return {
    LogoComponent: category.logo,
    id: category.id,
    items,
    label
  };
}
function toPatternGroups(patternSections: ReturnType<typeof useRequestCatalogByCategory>['patternSections']): RequestCategoryGroup[] {
  return patternSections.map(section => ({
    LogoComponent: PatternCatalogSectionLogo,
    id: `pattern-section-${section.operationPackageId}`,
    items: section.items,
    label: section.title
  }));
}
function RequestPageGroups({
  results,
  categoryLabels
}: {
  results: ReturnType<typeof useAllowIndividualRequestOperationPackagesQueries>;
  categoryLabels: Record<CategoryTranslationKey, string>;
}) {
  const laborCatalog = useRequestCatalogByCategory(results[0].data);
  const contractorCatalog = useRequestCatalogByCategory(results[1].data);
  const itCatalog = useRequestCatalogByCategory(results[2].data);
  const gaCatalog = useRequestCatalogByCategory(results[3].data);

  // 製品カテゴリの直後に、そのカテゴリから切り出したパターンセクションを挿入
  // （例: IT → 申請ワークフロー → GA）
  const groups = [toProductGroup(CATEGORIES[0], laborCatalog.productItems, categoryLabels[CATEGORIES[0].translationKey]), ...toPatternGroups(laborCatalog.patternSections), toProductGroup(CATEGORIES[1], contractorCatalog.productItems, categoryLabels[CATEGORIES[1].translationKey]), ...toPatternGroups(contractorCatalog.patternSections), toProductGroup(CATEGORIES[2], itCatalog.productItems, categoryLabels[CATEGORIES[2].translationKey]), ...toPatternGroups(itCatalog.patternSections), toProductGroup(CATEGORIES[3], gaCatalog.productItems, categoryLabels[CATEGORIES[3].translationKey]), ...toPatternGroups(gaCatalog.patternSections)].filter((group): group is RequestCategoryGroup => group != null);
  if (groups.length === 0) {
    return <EmptyState />;
  }
  return <RequestsListCard groups={groups} />;
}
function RequestPage() {
  const {
    t
  } = useTranslation(defaultNS, {
    keyPrefix: 'individualPages.request.categories'
  });
  const results = useAllowIndividualRequestOperationPackagesQueries(CATEGORY_OWNER_PRODUCTS);
  const allEmpty = results.every(result => result.data.length === 0);
  const categoryLabels: Record<CategoryTranslationKey, string> = {
    contractorCore: t('contractorCore'),
    gaOperationCore: t('gaOperationCore'),
    itOperationCore: t('itOperationCore'),
    laborCore: t('laborCore')
  };
  return <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-[1280px] px-4 py-6 md:px-[100px] md:py-10">
        <div className="mx-auto flex w-full max-w-[800px] flex-col gap-9">
          {/* TODO: 検索機能は未配線のため一旦無効化。配線時に RequestSearchBar を復活させる */}
          {allEmpty ? <EmptyState /> : <RequestPageGroups results={results} categoryLabels={categoryLabels} />}
        </div>
      </div>
    </div>;
}